import type { VisionData, VisionTemplate, VisionFieldDef, VisionFieldType } from '@/modules/vision/template-types';

export const DEFAULT_VISION_TEMPLATE: VisionTemplate = {
  fields: [
    { id: 'core-values', type: 'detailed_list', title: 'Core Values' },
    {
      id: 'core-focus', type: 'field_group', title: 'Core Focus',
      children: [
        { id: 'core-focus-purpose', type: 'long_text', title: 'Purpose / Cause / Passion' },
        { id: 'core-focus-niche',   type: 'long_text', title: 'Our Niche' },
      ],
    },
    { id: 'ten-year-target', type: 'long_text', title: '10-Year Target' },
    {
      id: 'marketing', type: 'field_group', title: 'Marketing Strategy',
      children: [
        { id: 'marketing-target-market',   type: 'long_text',   title: 'Target Market / Ideal Customer' },
        { id: 'marketing-three-uniques',   type: 'simple_list', title: 'Unique Differentiators' },
        { id: 'marketing-proven-process',  type: 'long_text',   title: 'Proven Process' },
        { id: 'marketing-guarantee',       type: 'long_text',   title: 'Guarantee' },
      ],
    },
    {
      id: 'three-year', type: 'field_group', title: '3-Year Picture',
      children: [
        { id: 'three-year-future-date',  type: 'date',        title: 'Future Date',              width: 'half' },
        { id: 'three-year-revenue',      type: 'short_text',  title: 'Revenue',                  width: 'half' },
        { id: 'three-year-profit',       type: 'short_text',  title: 'Profit',                   width: 'half' },
        { id: 'three-year-measurables',  type: 'simple_list', title: 'Metrics' },
        { id: 'three-year-looks-like',   type: 'long_text',   title: 'What does it look like?' },
      ],
    },
    {
      id: 'one-year', type: 'field_group', title: '1-Year Plan',
      children: [
        { id: 'one-year-future-date',  type: 'date',         title: 'Future Date',  width: 'half' },
        { id: 'one-year-revenue',      type: 'short_text',   title: 'Revenue',      width: 'half' },
        { id: 'one-year-profit',       type: 'short_text',   title: 'Profit',       width: 'half' },
        { id: 'one-year-measurables',  type: 'simple_list',  title: 'Metrics' },
        { id: 'one-year-goals',        type: 'annual_goals', title: 'Goals for the Year' },
      ],
    },
  ],
};

export const PRESET_VISION_TEMPLATES: Record<string, VisionTemplate> = {
  scaling_up: {
    fields: [
      { id: 'core-values', type: 'detailed_list', title: 'Core Values' },
      { id: 'purpose', type: 'long_text', title: 'Purpose' },
      { id: 'bhag', type: 'long_text', title: 'BHAG' },
      { id: 'brand-promises', type: 'simple_list', title: 'Brand Promises' },
      { id: 'brand-promise-guarantee', type: 'long_text', title: 'Guarantee' },
      {
        id: 'targets', type: 'field_group', title: '3–5 Year Targets',
        children: [
          { id: 'targets-future-date', type: 'date',        title: 'Future Date', width: 'half' },
          { id: 'targets-revenue',     type: 'short_text',  title: 'Revenue',     width: 'half' },
          { id: 'targets-profit',      type: 'short_text',  title: 'Profit',      width: 'half' },
          { id: 'targets-key-thrusts', type: 'simple_list', title: 'Key Thrusts' },
        ],
      },
      {
        id: 'one-year', type: 'field_group', title: '1-Year Plan',
        children: [
          { id: 'one-year-future-date', type: 'date',         title: 'Future Date',        width: 'half' },
          { id: 'one-year-revenue',     type: 'short_text',   title: 'Revenue',            width: 'half' },
          { id: 'one-year-profit',      type: 'short_text',   title: 'Profit',             width: 'half' },
          { id: 'one-year-kpis',        type: 'simple_list',  title: 'KPIs' },
          { id: 'one-year-goals',       type: 'annual_goals', title: 'Annual Initiatives' },
        ],
      },
      { id: 'critical-number', type: 'short_text', title: 'Critical Number' },
    ],
  },
};

export function visionHasContent(data: VisionData | null | undefined): boolean {
  return Object.values(data ?? {}).some((value) => {
    if (value == null || value === false) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.length > 0;
    return true;
  });
}

/** All leaf field defs (excludes field_group containers and annual_goals). */
export function flattenDataFields(fields: VisionFieldDef[]): VisionFieldDef[] {
  const result: VisionFieldDef[] = [];
  for (const f of fields) {
    if (f.type === 'field_group') {
      result.push(...flattenDataFields(f.children ?? []));
    } else if (f.type !== 'annual_goals' && f.type !== 'spacer') {
      result.push(f);
    }
  }
  return result;
}

/**
 * Which field on THIS document answers a tenant-level field setting (#150).
 *
 * `home_vision_field_id` and `home_values_field_id` are single workspace-level
 * keys into vision `data`, chosen when there was one document per tenant. Once
 * vision resolves up the team chain there can be several, each carrying its own
 * template — and a team whose document uses a different template rendered an
 * empty bulletin, because the configured id simply was not in it.
 *
 * Blank is the wrong answer there. The setting expresses "show the purpose-ish
 * field", so when the exact id is absent this falls back to the first field of
 * the requested TYPE in the document's own template: a long_text for the
 * purpose, a detailed_list for the values. Same intent, resolved against the
 * document actually on screen.
 *
 * Null only when the template has no field of that type at all, which is the one
 * case where there is genuinely nothing to show.
 */
export function resolveVisionFieldId(
  template: VisionTemplate | null | undefined,
  preferredId: string | undefined,
  type: VisionFieldType,
): string | null {
  const fields = flattenDataFields(template?.fields ?? DEFAULT_VISION_TEMPLATE.fields);
  if (preferredId && fields.some((f) => f.id === preferredId && f.type === type)) return preferredId;
  return fields.find((f) => f.type === type)?.id ?? null;
}
